import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'motion/react';
import {
  CalendarPlus,
  Calendar,
  Mail,
  Send,
  X,
  Building2,
  Briefcase,
  MessageSquare,
  Inbox,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const capitalize = (s) => {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const demoStatusColor = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'new' || v === 'contacted') return 'bg-blue-100 text-blue-700';
  if (v === 'scheduled') return 'bg-indigo-100 text-indigo-700';
  if (v === 'onboarded' || v === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (v === 'declined') return 'bg-gray-100 text-gray-500';
  return 'bg-gray-100 text-gray-600';
};

const STATUS_TABS = [
  { id: 'new_contacted', label: 'New & Contacted', statusParam: 'new,contacted' },
  { id: 'scheduled', label: 'Scheduled', statusParam: 'scheduled' },
  { id: 'onboarded', label: 'Onboarded', statusParam: 'onboarded,completed' },
  { id: 'declined', label: 'Declined', statusParam: 'declined' },
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'onboarded', label: 'Onboarded' },
  { value: 'declined', label: 'Declined' },
];

function Spinner({ className }) {
  return (
    <svg
      className={cn('animate-spin h-5 w-5 text-[#007BFF]', className)}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

export default function InternalDemo() {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('new_contacted');
  const [inviteModal, setInviteModal] = useState(null);
  useBodyScrollLock(Boolean(inviteModal));
  const [inviteForm, setInviteForm] = useState({
    date: '',
    time: '10:00',
    duration: '30',
    meetingLink: '',
    notes: '',
  });
  const [sending, setSending] = useState(false);

  const statusParam = STATUS_TABS.find((t) => t.id === activeTab)?.statusParam || 'new,contacted';

  const fetchDemos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/demo-requests?status=${statusParam}`);
      setDemos(data.requests || []);
    } catch {
      setDemos([]);
    } finally {
      setLoading(false);
    }
  }, [statusParam]);

  useEffect(() => {
    fetchDemos();
  }, [fetchDemos]);

  const handleStatusChange = async (id, status) => {
    try {
      await axios.patch(`/api/demo-requests/${id}`, { status });
      toast.success(`Status updated to ${capitalize(status)}`);
      fetchDemos();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleSendInvite = async () => {
    if (!inviteForm.date || !inviteForm.time) {
      toast.error('Date and time are required');
      return;
    }
    setSending(true);
    try {
      await axios.post(`/api/demo-requests/${inviteModal._id}/send-invite`, inviteForm);
      toast.success('Calendar invite sent!');
      setInviteModal(null);
      setInviteForm({ date: '', time: '10:00', duration: '30', meetingLink: '', notes: '' });
      fetchDemos();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const displayStatus = (s) => (s === 'completed' ? 'Onboarded' : capitalize(s));

  const inputCls =
    'w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 outline-none transition';

  return (
    <div className="min-h-full">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Demo Requests</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage demo sign-ups, schedule calls, and send calendar invites.
        </p>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'rounded-xl px-4 py-2.5 text-sm font-medium transition-all',
              activeTab === tab.id
                ? 'bg-[#007BFF] text-white shadow-sm'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner className="h-8 w-8" />
        </div>
      ) : demos.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-2xl border-2 border-dashed border-gray-200 bg-white/50 py-16 text-center"
        >
          <Inbox className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-3 text-sm font-medium text-gray-500">No demo requests in this category</p>
          <p className="mt-1 text-xs text-gray-400">Try another status or wait for new sign-ups</p>
        </motion.div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
          <AnimatePresence mode="popLayout">
            {demos.map((d, i) => (
              <motion.div
                key={d._id}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, delay: i * 0.03 }}
                className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-gray-900">{d.name}</span>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          demoStatusColor(d.status)
                        )}
                      >
                        {displayStatus(d.status)}
                      </span>
                    </div>
                    {d.company && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                        <span>{d.company}</span>
                      </div>
                    )}
                    {d.designation && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Briefcase className="h-4 w-4 text-gray-400 shrink-0" />
                        <span>{d.designation}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Mail className="h-4 w-4 text-gray-400 shrink-0" />
                      <a href={`mailto:${d.email}`} className="text-[#007BFF] hover:underline truncate">
                        {d.email}
                      </a>
                    </div>
                    {(d.monthlyHires || d.interests?.length > 0) && (
                      <p className="text-xs text-gray-500">
                        {d.monthlyHires && <>Hires {d.monthlyHires} a month</>}
                        {d.monthlyHires && d.interests?.length > 0 && ' · '}
                        {d.interests?.length > 0 && <>Wants to see {d.interests.join(', ')}</>}
                      </p>
                    )}
                    {d.heardFrom && (
                      <p className="text-xs text-gray-500">Via {d.heardFrom}</p>
                    )}
                    {d.message && (
                      <div className="flex gap-2 text-sm text-gray-600">
                        <MessageSquare className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                        <p className="line-clamp-2">{d.message}</p>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                      <span className="tabular-nums">Requested {fmtDate(d.createdAt)}</span>
                      {d.scheduledAt && (
                        <span className="inline-flex items-center gap-1 text-indigo-600 font-medium">
                          <Calendar className="h-3.5 w-3.5" />
                          Scheduled {fmtDate(d.scheduledAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 sm:flex-row sm:items-center">
                    <select
                      value={d.status === 'completed' ? 'onboarded' : d.status}
                      onChange={(e) => handleStatusChange(d._id, e.target.value)}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 outline-none"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        setInviteModal(d);
                        setInviteForm({
                          date: '',
                          time: '10:00',
                          duration: '30',
                          meetingLink: '',
                          notes: '',
                        });
                      }}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#007BFF] text-white px-4 py-2 text-sm font-medium hover:bg-blue-600 transition-colors"
                    >
                      <CalendarPlus className="h-4 w-4" />
                      Send Invite
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <AnimatePresence>
        {inviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setInviteModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-gray-900">Schedule Demo & Send Invite</h3>
                <button
                  type="button"
                  onClick={() => setInviteModal(null)}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Sending calendar invite to <strong>{inviteModal.name}</strong> ({inviteModal.email})
              </p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Date *</label>
                    <input
                      type="date"
                      value={inviteForm.date}
                      onChange={(e) => setInviteForm((f) => ({ ...f, date: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Time *</label>
                    <input
                      type="time"
                      value={inviteForm.time}
                      onChange={(e) => setInviteForm((f) => ({ ...f, time: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Duration (minutes)
                  </label>
                  <select
                    value={inviteForm.duration}
                    onChange={(e) =>
                      setInviteForm((f) => ({ ...f, duration: e.target.value }))
                    }
                    className={inputCls}
                  >
                    <option value="15">15 min</option>
                    <option value="30">30 min</option>
                    <option value="45">45 min</option>
                    <option value="60">60 min</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Meeting Link</label>
                  <input
                    type="url"
                    value={inviteForm.meetingLink}
                    onChange={(e) =>
                      setInviteForm((f) => ({ ...f, meetingLink: e.target.value }))
                    }
                    className={inputCls}
                    placeholder="https://meet.google.com/..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
                  <textarea
                    value={inviteForm.notes}
                    onChange={(e) =>
                      setInviteForm((f) => ({ ...f, notes: e.target.value }))
                    }
                    className={cn(inputCls, 'resize-none')}
                    rows={2}
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setInviteModal(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSendInvite}
                  disabled={sending || !inviteForm.date || !inviteForm.time}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium text-white transition-colors',
                    sending || !inviteForm.date || !inviteForm.time
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-[#007BFF] hover:bg-blue-600'
                  )}
                >
                  {sending ? (
                    <Spinner className="h-4 w-4 text-white" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {sending ? 'Sending…' : 'Send Calendar Invite'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
